import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, Upload, Trash2, Check, Image as ImageIcon, 
  Star, AlertCircle, Loader2
} from 'lucide-react';
import { inventoryAPI } from '../../api';
import toast from 'react-hot-toast';
import { MAX_UPLOAD_MB, MAX_UPLOAD_BYTES } from '../../config/uploadLimits';
import { compressForCatalog } from '../../utils/imageUtils';

const InventoryImages = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [images, setImages] = useState([]);
  const [tryonInputId, setTryonInputId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [productRes, imagesRes] = await Promise.all([
        inventoryAPI.getProduct(productId),
        inventoryAPI.listImages(productId)
      ]);
      setProduct(productRes.data);
      setImages(imagesRes.data.images);
      setTryonInputId(imagesRes.data.tryon_input_image_id);
    } catch (error) {
      toast.error('Failed to load product data');
      navigate('/partners/inventory');
    } finally {
      setLoading(false);
    }
  }, [productId, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const files = e.dataTransfer?.files;
    if (files?.length) {
      await uploadFiles(Array.from(files));
    }
  };

  const handleFileInput = async (e) => {
    const files = e.target.files;
    if (files?.length) {
      await uploadFiles(Array.from(files));
    }
    e.target.value = '';
  };

  const uploadFiles = async (files) => {
    const imageFiles = files.filter(f => f.type.startsWith('image/'));
    
    if (imageFiles.length === 0) {
      toast.error('Please select image files');
      return;
    }

    setUploading(true);
    
    for (const file of imageFiles) {
      if (file.size > MAX_UPLOAD_BYTES) {
        toast.error(`${file.name} is too large (max ${MAX_UPLOAD_MB}MB)`);
        continue;
      }

      try {
        // Compress before upload to stay under AWS API Gateway's ~6 MB
        // sync-invoke cap (file.size has already been validated against
        // MAX_UPLOAD_BYTES above).
        const compressed = await compressForCatalog(file);
        const formData = new FormData();
        formData.append('file', compressed, compressed.name || file.name);
        // If no images yet, set as tryon input
        formData.append('is_tryon_input', images.length === 0 ? 'true' : 'false');

        await inventoryAPI.uploadImage(productId, formData);
        toast.success(`Uploaded ${file.name}`);
      } catch (error) {
        console.error('Image upload failed', error);
        toast.error(`Failed to upload ${file.name}`);
      }
    }

    setUploading(false);
    fetchData();
  };

  const handleSetTryonInput = async (imageId) => {
    try {
      await inventoryAPI.setTryonInput(productId, imageId);
      setTryonInputId(imageId);
      toast.success('Try-On input image updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update Try-On input');
    }
  };

  const handleDeleteImage = async (imageId) => {
    if (!window.confirm('Delete this image?')) return;

    try {
      await inventoryAPI.deleteImage(productId, imageId);
      toast.success('Image deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete image');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link
          to={`/partners/inventory/${productId}`}
          className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-slate-900">Manage Images</h1>
          <p className="text-slate-500">{product?.product_name} ({product?.sku_id})</p>
        </div>
      </div>

      {/* Upload Area */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
          dragActive 
            ? 'border-blue-500 bg-blue-50' 
            : 'border-slate-200 hover:border-slate-300'
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        {uploading ? (
          <div className="flex flex-col items-center">
            <Loader2 className="w-12 h-12 text-blue-500 animate-spin mb-4" />
            <p className="text-slate-600">Uploading images...</p>
          </div>
        ) : (
          <>
            <Upload className="w-12 h-12 text-slate-400 mx-auto mb-4" />
            <p className="text-slate-600 mb-2">
              Drag and drop images here, or click to browse
            </p>
            <p className="text-sm text-slate-400 mb-4">
              PNG, JPG, WebP up to 10MB each
            </p>
            <label className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer">
              <Upload className="w-5 h-5" />
              Choose Files
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileInput}
                className="hidden"
              />
            </label>
          </>
        )}
      </motion.div>

      {/* Helper Text */}
      <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 text-sm text-blue-700">
        <p className="font-medium mb-1">💡 Select Try-On Input Image</p>
        <p>Upload multiple images for your dress. Select one image as the Try-On input that will be used when customers try on your product.</p>
      </div>

      {/* Images Grid */}
      {images.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <ImageIcon className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-900 mb-2">No images yet</h3>
          <p className="text-slate-500">
            Upload at least one image to activate this product
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {images.map((image) => (
            <motion.div
              key={image.image_id}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className={`relative group bg-white rounded-xl border-2 overflow-hidden ${
                image.is_tryon_input 
                  ? 'border-blue-500 ring-2 ring-blue-100' 
                  : 'border-slate-200'
              }`}
            >
              <div className="aspect-square">
                <img
                  src={image.image_url}
                  alt="Product"
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Try-On Badge */}
              {image.is_tryon_input && (
                <div className="absolute top-2 left-2 bg-blue-600 text-white text-xs font-medium px-2 py-1 rounded-full flex items-center gap-1">
                  <Star className="w-3 h-3 fill-current" />
                  Try-On Input
                </div>
              )}

              {/* Actions Overlay */}
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                {!image.is_tryon_input && (
                  <button
                    onClick={() => handleSetTryonInput(image.image_id)}
                    className="p-2 bg-white rounded-lg text-blue-600 hover:bg-blue-50"
                    title="Set as Try-On Input"
                  >
                    <Star className="w-5 h-5" />
                  </button>
                )}
                <button
                  onClick={() => handleDeleteImage(image.image_id)}
                  className="p-2 bg-white rounded-lg text-red-600 hover:bg-red-50"
                  title="Delete"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Warning if no tryon input */}
      {images.length > 0 && !tryonInputId && (
        <div className="bg-amber-50 border border-amber-100 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-amber-800">No Try-On input selected</p>
            <p className="text-sm text-amber-700">
              Click the star icon on an image to set it as the Try-On input. This is required to activate the product.
            </p>
          </div>
        </div>
      )}

      {/* Navigation */}
      <div className="flex items-center justify-between pt-4 border-t border-slate-100">
        <Link
          to={`/partners/inventory/${productId}`}
          className="px-4 py-2 border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50"
        >
          Back to Product
        </Link>
        <Link
          to={`/partners/inventory/${productId}/variants`}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Manage Variants →
        </Link>
      </div>
    </div>
  );
};

export default InventoryImages;
